import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { SearchIcon } from 'lucide-react';
import { toast } from 'sonner';

import { receivingApi } from 'api/receiving';
import { UserContext } from 'contexts/UserContext';
import { Alert, AlertDescription } from 'shadcn/components/ui/Alert';
import { Button } from 'shadcn/components/ui/Button';
import { Card, CardContent } from 'shadcn/components/ui/Card';
import { Input } from 'shadcn/components/ui/Input';

import { DocumentUploadDialog } from './DocumentUploadDialog';
import { PurchaseOrderDocumentsCard } from './PurchaseOrderDocumentsCard';

function PurchaseOrderDocumentsV2() {
    const [searchValue, setSearchValue] = useState('');
    const [searching, setSearching] = useState(false);
    const [poData, setPoData] = useState(null);
    const [error, setError] = useState(null);
    const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
    const user = useContext(UserContext);
    const currentPoIdRef = useRef(null);

    useEffect(() => {
        currentPoIdRef.current = poData?.id ?? null;
    }, [poData?.id]);

    const fetchPurchaseOrder = useCallback(
        async (poID, { showSuccessToast = true } = {}) => {
            setSearching(true);
            setError(null);
            setPoData(null);

            try {
                const response = await receivingApi.getReceivableGroceryAndPackagingPurchaseOrders(
                    user.getFacilityID(),
                    true,
                    poID,
                );

                if (response.status === false) {
                    const msg = response.msg || 'Something went wrong';
                    setError(msg);
                    return;
                }

                // The API returns an array of rows, get the first one
                const poRow = response.data?.rows?.[0];

                if (!poRow) {
                    setError('Purchase order not found in this facility');
                    return;
                }

                // Add facilityName from context
                const poDataWithFacility = {
                    ...poRow,
                    facilityName: user.getFacilityName(),
                };

                setPoData(poDataWithFacility);
                if (showSuccessToast) {
                    toast.success(`Purchase order #${poID} loaded successfully`);
                }
            } catch (err) {
                setError('An error occurred while searching for the purchase order');
                console.error('Search error:', err);
            } finally {
                setSearching(false);
            }
        },
        [user],
    );

    const handleSearch = useCallback(
        async (e) => {
            e.preventDefault();

            if (!searchValue.trim()) {
                toast.error('Please enter a PO ID');
                return;
            }

            const poID = parseInt(searchValue.trim(), 10);
            if (Number.isNaN(poID) || poID <= 0) {
                toast.error('PO ID must be a positive number');
                return;
            }

            await fetchPurchaseOrder(poID);
        },
        [fetchPurchaseOrder, searchValue],
    );

    const handleUploadSuccess = useCallback(
        (uploadedPoID) => {
            // Refresh only if the user is still viewing the same PO
            if (currentPoIdRef.current === uploadedPoID) {
                fetchPurchaseOrder(uploadedPoID, { showSuccessToast: false });
            }
        },
        [fetchPurchaseOrder],
    );

    const handleUploadClick = useCallback(() => {
        setUploadDialogOpen(true);
    }, []);

    const hasPoData = Boolean(poData?.id);

    return (
        <div className="space-y-6 mt-6">
            <div className="space-y-2">
                <p className="text-gray-600">Search for a purchase order to view and manage its documents</p>
            </div>

            {/* Search Section */}
            <Card>
                <CardContent className="pt-6">
                    <form onSubmit={handleSearch} className="flex gap-3">
                        <div className="flex-1 relative">
                            <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-500" />
                            <Input
                                type="text"
                                placeholder="Enter Purchase Order ID (e.g., 12345)"
                                value={searchValue}
                                onChange={(e) => setSearchValue(e.target.value)}
                                className="pl-10"
                                disabled={searching}
                            />
                        </div>
                        <Button type="submit" disabled={searching} className="gap-2">
                            {searching ? (
                                <>
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                                    Searching...
                                </>
                            ) : (
                                <>
                                    <SearchIcon className="h-4 w-4" />
                                    Search
                                </>
                            )}
                        </Button>
                    </form>
                </CardContent>
            </Card>

            {/* Error Alert */}
            {error && !poData && (
                <Alert variant="destructive">
                    <AlertDescription>{error}</AlertDescription>
                </Alert>
            )}

            {/* PO Details Card */}
            {hasPoData && <PurchaseOrderDocumentsCard poData={poData} onUploadClick={handleUploadClick} />}

            {/* Upload Dialog */}
            {hasPoData && (
                <DocumentUploadDialog
                    isOpen={uploadDialogOpen}
                    onClose={() => setUploadDialogOpen(false)}
                    poID={poData.id}
                    onUploadSuccess={handleUploadSuccess}
                />
            )}
        </div>
    );
}

export { PurchaseOrderDocumentsV2 };
